import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixStoresCatalogV3Customization from './stores-catalog-v3-customization-customizations-v-3.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * Customizations include options and modifiers which can later be applied to products.
 * Options are designed to add variations to a product, where modifiers add a customizable change to the product but without creating another variant.
 */
export interface Customization extends CustomizationCustomizationSettingsOneOf {
  /**
   * Free text input settings.
   *
   * > **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.
   * @immutable
   */
  freeTextInput?: FreeTextSettings;
  /**
   * Choices settings.
   *
   * > **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.
   */
  choicesSettings?: ChoicesSettings;
  /**
   * Customization ID.
   * @minLength 1
   * @maxLength 36
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the customization is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the customization.
   *
   * Ignored when creating a customization.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the customization was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the customization was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * A read-only identifier generated from the customization name.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 50
   */
  key?: string | null;
  /**
   * Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /**
   * Customization type.
   * @immutable
   */
  customizationType?: CustomizationTypeWithLiterals;
  /**
   * Customization render type.
   *
   * Defines how the customization will be displayed in the storefront.
   * @immutable
   */
  customizationRenderType?: CustomizationRenderTypeWithLiterals;
  /**
   * Number of products this customization is assigned to.
   * > **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.
   * @readonly
   */
  assignedProductsCount?: number | null;
}

/** @oneof */
export interface CustomizationCustomizationSettingsOneOf {
  /**
   * Free text input settings.
   *
   * > **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.
   * @immutable
   */
  freeTextInput?: FreeTextSettings;
  /**
   * Choices settings.
   *
   * > **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.
   */
  choicesSettings?: ChoicesSettings;
}

export enum CustomizationType {
  PRODUCT_OPTION = 'PRODUCT_OPTION',
  MODIFIER = 'MODIFIER',
}

/** @enumType */
export type CustomizationTypeWithLiterals =
  | CustomizationType
  | 'PRODUCT_OPTION'
  | 'MODIFIER';

export enum CustomizationRenderType {
  /** Customization by free text input. */
  FREE_TEXT = 'FREE_TEXT',
  /** Customization by selection of text choices. */
  TEXT_CHOICES = 'TEXT_CHOICES',
  /** Customization by selection of colors. */
  SWATCH_CHOICES = 'SWATCH_CHOICES',
}

/** @enumType */
export type CustomizationRenderTypeWithLiterals =
  | CustomizationRenderType
  | 'FREE_TEXT'
  | 'TEXT_CHOICES'
  | 'SWATCH_CHOICES';

export interface FreeTextSettings {
  /** Minimum text character length. */
  minCharCount?: number;
  /**
   * Maximum text character length.
   * @max 500
   */
  maxCharCount?: number;
  /**
   * Default amount added to a product's price when this choice is assigned to a modifier.
   * @decimalValue options { gte:0, lte:999999999, maxScale:3 }
   */
  defaultAddedPrice?: string | null;
  /**
   * Title to display to customer for their free-text input.
   * @minLength 1
   * @maxLength 100
   */
  title?: string;
  /**
   * A read-only identifier generated from the title.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 150
   */
  key?: string | null;
}

/** Settings for choice-based customizations. */
export interface ChoicesSettings {
  /**
   * List of choices.
   * @minSize 1
   * @maxSize 200
   * @immutable
   */
  choices?: Choice[];
  /**
   * Strategy for sorting choices in storefront filter panels. Use [Update Customization](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/customizations-v3/update-customization) to change this value.
   * `sortOrder` is set to `MANUAL` when calling [Reorder Customization Choices](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/customizations-v3/reorder-customization-choices).
   *
   * Default: `DEFAULT`
   * @internal
   */
  sortOrder?: ChoiceSortStrategyWithLiterals;
}

export interface Choice extends ChoiceValueOneOf {
  /**
   * Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).
   * @minLength 3
   * @maxLength 20
   * @format COLOR_HEX
   */
  colorCode?: string;
  /**
   * Choice ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Choice type.
   *
   * > **Notes:**
   * > + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.
   * > + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.
   * @immutable
   */
  choiceType?: ChoiceTypeWithLiterals;
  /**
   * A read-only identifier generated from the choice name.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 50
   */
  key?: string | null;
  /**
   * Choice name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /**
   * Default amount added to a product's price when this customization is assigned to a modifier.
   * @decimalValue options { gte:0, lte:999999999, maxScale:3 }
   */
  defaultAddedPrice?: string | null;
  /**
   * Optional image displayed alongside a ONE_COLOR choice in the storefront.
   * Only valid for ONE_COLOR choices; not populated for other choice types.
   * @internal
   */
  displayImage?: string;
  /**
   * IDs of primary choices that this choice is grouped under for storefront filtering.
   * A choice with `primaryChoiceIds` is considered a "linked" choice.
   * Linked choices are excluded from the top-level storefront filter list
   * and displayed only under their primary choice groups.
   *
   * A linked choice can have multiple primaries, in which case it appears under each primary group.
   * Only a single level of primary-linked grouping is supported. A choice that is a primary can't also be a linked choice.
   * @internal
   * @format GUID
   * @maxSize 4
   */
  primaryChoiceIds?: string[];
}

/** @oneof */
export interface ChoiceValueOneOf {
  /**
   * Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).
   * @minLength 3
   * @maxLength 20
   * @format COLOR_HEX
   */
  colorCode?: string;
}

export enum ChoiceType {
  /** For a `customizationRenderType` of `TEXT_CHOICES`. */
  CHOICE_TEXT = 'CHOICE_TEXT',
  /** For `customizationRenderType: SWATCH_CHOICES`. */
  ONE_COLOR = 'ONE_COLOR',
}

/** @enumType */
export type ChoiceTypeWithLiterals = ChoiceType | 'CHOICE_TEXT' | 'ONE_COLOR';

export interface MultipleColors {
  /**
   * A list of color codes.
   * @minLength 3
   * @maxLength 20
   * @minSize 2
   * @maxSize 2
   */
  colorCodes?: string[];
}

export interface FocalPoint {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
  /** crop by height */
  height?: number | null;
  /** crop by width */
  width?: number | null;
}

export enum ChoiceSortStrategy {
  /** Default. Text choices are sorted by name, swatch choices are sorted by product count. */
  DEFAULT = 'DEFAULT',
  /** Alphabetically by name, case-insensitive. Numeric values are sorted first. */
  BY_NAME = 'BY_NAME',
  /** By the number of products assigned to each choice, in descending order. */
  BY_PRODUCT_COUNT = 'BY_PRODUCT_COUNT',
  /** By the order of the choices list. Use [Reorder Customization Choices](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/customizations-v3/reorder-customization-choices) to set the order. */
  MANUAL = 'MANUAL',
}

/** @enumType */
export type ChoiceSortStrategyWithLiterals =
  | ChoiceSortStrategy
  | 'DEFAULT'
  | 'BY_NAME'
  | 'BY_PRODUCT_COUNT'
  | 'MANUAL';

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface CreateCustomizationRequest {
  /** Customization to create. */
  customization: Customization;
}

export interface CreateCustomizationResponse {
  /** Created customization. */
  customization?: Customization;
}

export interface GetCustomizationRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export enum RequestedFields {
  /** Assigned products count. */
  ASSIGNED_PRODUCTS_COUNT = 'ASSIGNED_PRODUCTS_COUNT',
}

/** @enumType */
export type RequestedFieldsWithLiterals =
  | RequestedFields
  | 'ASSIGNED_PRODUCTS_COUNT';

export interface GetCustomizationResponse {
  /** Customization. */
  customization?: Customization;
}

export interface UpdateCustomizationRequest {
  /** Customization to update. */
  customization: Customization;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface UpdateCustomizationResponse {
  /** Updated customization. */
  customization?: Customization;
}

export interface DeleteCustomizationRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
}

export interface DeleteCustomizationResponse {}

export interface QueryCustomizationsRequest {
  /** Query options. */
  query?: CursorQuery;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  /** Ascending order. */
  ASC = 'ASC',
  /** Descending order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryCustomizationsResponse {
  /**
   * List of customizations.
   * @minSize 1
   * @maxSize 200
   */
  customizations?: Customization[];
  /** Details on the paged set of results returned. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkCreateCustomizationsRequest {
  /**
   * Customizations to create.
   * @minSize 1
   * @maxSize 100
   */
  customizations: Customization[];
  /**
   * Whether to return the full customization entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface BulkCreateCustomizationsResponse {
  /**
   * Customizations created by bulk action.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkCustomizationResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCustomizationResult {
  /** Bulk action metadata for customization. */
  itemMetadata?: ItemMetadata;
  /**
   * Full customization entity.
   *
   * Returned only if `returnEntity: true` is passed in the request.
   */
  customization?: Customization;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @minLength 1
   * @maxLength 36
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface CustomizationIdsWrapper {
  /**
   * list of all the customization ids that are invalid
   * e.g list of failed choices ids
   * @minSize 1
   * @maxSize 100
   * @minLength 1
   * @maxLength 36
   */
  customizationIds?: string[];
}

export interface BulkCreateCustomizationsForMigrationRequest {
  /**
   * Customizations to create.
   * @minSize 1
   * @maxSize 100
   */
  customizations?: Customization[];
  /**
   * Whether to return the full customization entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface BulkCreateCustomizationsForMigrationResponse {
  /**
   * Customizations created by bulk action.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkCustomizationResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface AddCustomizationChoicesRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /**
   * Choices to add.
   * @minSize 1
   * @maxSize 100
   */
  choices: Choice[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface AddCustomizationChoicesResponse {
  /** Updated customization. */
  customization?: Customization;
}

export interface SetCustomizationChoicesRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /**
   * Choices to set.
   * @minSize 1
   * @maxSize 200
   */
  choices: Choice[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface SetCustomizationChoicesResponse {
  /** Updated customization. */
  customization?: Customization;
}

export interface RemoveCustomizationChoicesRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /**
   * IDs of choices to remove.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  choiceIds: string[];
  /** Customization revision. */
  revision?: string;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface RemoveCustomizationChoicesResponse {
  /** Updated customization. */
  customization?: Customization;
}

export interface UpdateCustomizationChoicesRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /**
   * Revision number, which increments by 1 each time the customization is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the customization.
   */
  revision: string;
  /**
   * Choices to update. Each item must include a `choice` with a valid `id` matching an existing choice in the customization.
   * @minSize 1
   * @maxSize 100
   */
  choices: MaskedChoice[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface MaskedChoice {
  /** Choice to update. */
  choice?: Choice;
}

export interface UpdateCustomizationChoicesResponse {
  /** Updated customization. */
  customization?: Customization;
}

export interface ReorderCustomizationChoicesRequest {
  /**
   * Customization ID.
   * @format GUID
   */
  customizationId: string;
  /** Revision number, which increments by 1 each time the customization is updated. To prevent conflicting changes, the current revision must be passed when updating the customization. */
  revision: string;
  /**
   * Choice IDs in the desired display order. Must contain exactly the same set of IDs as the customization's current choices — no additions or removals.
   * @format GUID
   * @minSize 1
   * @maxSize 200
   */
  choiceIds: string[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface ReorderCustomizationChoicesResponse {
  /** Updated customization. */
  customization?: Customization;
}

/** Additional error details returned with the `CHOICE_IDS_MISMATCH` error. */
export interface ChoiceIdsMismatchData {
  /** Choice IDs that exist in the customization but are missing from the request. */
  missingIds?: string[];
  /** Choice IDs included in the request that don't exist in the customization. */
  extraIds?: string[];
}

export interface BulkAddCustomizationChoicesRequest {
  /**
   * List of customization IDs and choices.
   * @minSize 1
   * @maxSize 100
   */
  customizationsChoices: CustomizationChoices[];
  /**
   * Whether to return the full customization entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CustomizationChoices {
  /**
   * Customization ID.
   * @minLength 1
   * @maxLength 36
   */
  customizationId?: string;
  /**
   * Choices to add.
   * @minSize 1
   * @maxSize 100
   */
  choices?: Choice[];
}

export interface BulkAddCustomizationChoicesResponse {
  /**
   * Customizations updated by bulk action.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkCustomizationResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateCustomizationsRequest {
  /**
   * List of customizations to update.
   * @minSize 1
   * @maxSize 100
   */
  customizations: MaskedCustomization[];
  /**
   * Whether to return the full customization entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface MaskedCustomization {
  /** Customization to update. */
  customization?: Customization;
}

export interface BulkUpdateCustomizationsResponse {
  /**
   * Customizations updated by bulk action.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkCustomizationResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface RecloneSiteRequest {
  /** @format GUID */
  cloneFrom?: string;
}

export interface RecloneSiteResponse {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateCustomizationApplicationErrors =
  | {
      code?: 'OPTIONS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MODIFIERS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_CUSTOMIZATION_RENDER_TYPE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_FREE_TEXT_SETTINGS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CreateCustomizationValidationErrors =
  | {
      ruleName?: 'ONLY_SPACES_STRING_ERROR';
    }
  | {
      ruleName?: 'DUPLICATE_CHOICE_NAME';
    }
  | {
      ruleName?: 'CUSTOMIZATION_WITH_ONLY_ID_OR_CHOICE_IDS';
    }
  | {
      ruleName?: 'MIXED_CHOICE_IDS_CONSISTENCY';
    }
  | {
      ruleName?: 'ID_NOT_ALLOWED_IN_REQUEST';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };
/** @docsIgnore */
export type DeleteCustomizationValidationErrors = {
  ruleName?: 'NOT_ALLOWED_DELETE_CUSTOMIZATION_ASSIGNED_TO_PRODUCT';
};
/** @docsIgnore */
export type BulkCreateCustomizationsApplicationErrors =
  | {
      code?: 'OPTIONS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MODIFIERS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DOESNT_SUPPORT_CHOICES';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CHOICES_LIMIT_EXCEEDED';
      description?: string;
      data?: CustomizationIdsWrapper;
    };
/** @docsIgnore */
export type BulkCreateCustomizationsValidationErrors =
  | {
      ruleName?: 'ONLY_SPACES_STRING_ERROR';
    }
  | {
      ruleName?: 'INVALID_CHOICE_TYPE';
    }
  | {
      ruleName?: 'DUPLICATE_CHOICE_NAME';
    }
  | {
      ruleName?: 'INVALID_CHOICE_VALUE';
    }
  | {
      ruleName?: 'CUSTOMIZATION_WITH_ONLY_ID_OR_CHOICE_IDS';
    }
  | {
      ruleName?: 'MIXED_CHOICE_IDS_CONSISTENCY';
    }
  | {
      ruleName?: 'ID_NOT_ALLOWED_IN_REQUEST';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };
/** @docsIgnore */
export type AddCustomizationChoicesApplicationErrors =
  | {
      code?: 'CHOICES_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DOESNT_SUPPORT_CHOICES';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type AddCustomizationChoicesValidationErrors =
  | {
      ruleName?: 'INVALID_CHOICE_TYPE';
    }
  | {
      ruleName?: 'INVALID_CHOICE_VALUE';
    }
  | {
      ruleName?: 'DUPLICATE_CHOICE_NAME';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };
/** @docsIgnore */
export type SetCustomizationChoicesApplicationErrors =
  | {
      code?: 'DOESNT_SUPPORT_CHOICES';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CHOICES_LIMIT_EXCEEDED';
      description?: string;
      data?: CustomizationIdsWrapper;
    };
/** @docsIgnore */
export type SetCustomizationChoicesValidationErrors =
  | {
      ruleName?: 'INVALID_CHOICE_TYPE';
    }
  | {
      ruleName?: 'INVALID_CHOICE_VALUE';
    }
  | {
      ruleName?: 'NOT_ALLOWED_DELETE_CHOICE_ASSIGNED_TO_PRODUCT';
    }
  | {
      ruleName?: 'DUPLICATE_CHOICE_NAME';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };
/** @docsIgnore */
export type RemoveCustomizationChoicesApplicationErrors = {
  code?: 'CHOICE_ASSIGNED_TO_PRODUCT';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type RemoveCustomizationChoicesValidationErrors =
  | {
      ruleName?: 'NOT_ALLOWED_CUSTOMIZATION_WITHOUT_CHOICES';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_DELETE_REFERENCED';
    };
/** @docsIgnore */
export type UpdateCustomizationChoicesApplicationErrors = {
  code?: 'CHOICE_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateCustomizationChoicesValidationErrors =
  | {
      ruleName?: 'UNSUPPORTED_CHOICE_FIELD';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };
/** @docsIgnore */
export type ReorderCustomizationChoicesApplicationErrors =
  | {
      code?: 'DOESNT_SUPPORT_CHOICES';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CHOICE_IDS_MISMATCH';
      description?: string;
      data?: ChoiceIdsMismatchData;
    };
/** @docsIgnore */
export type BulkAddCustomizationChoicesApplicationErrors =
  | {
      code?: 'CHOICES_LIMIT_EXCEEDED';
      description?: string;
      data?: CustomizationIdsWrapper;
    }
  | {
      code?: 'DOESNT_SUPPORT_CHOICES';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkAddCustomizationChoicesValidationErrors =
  | {
      ruleName?: 'INVALID_CHOICE_TYPE';
    }
  | {
      ruleName?: 'INVALID_CHOICE_VALUE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_SELF_REFERENCE';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_NOT_FOUND';
    }
  | {
      ruleName?: 'PRIMARY_CHOICE_HIERARCHY_VIOLATION';
    };

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface CustomizationCreatedEnvelope {
  entity: Customization;
  metadata: EventMetadata;
}

/**
 * Triggered when a customization is created.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read customizations in v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CUSTOMIZATION_READ_LIMITED
 * @permissionScope Read v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_READ
 * @permissionScope Manage Products
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-PRODUCTS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Read Products
 * @permissionScopeId SCOPE.DC-STORES.READ-PRODUCTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Product write in v3 catalog
 * @permissionScopeId SCOPE.STORES.PRODUCT_WRITE
 * @permissionScope Read customizations in v3 catalog
 * @permissionScopeId SCOPE.STORES.CUSTOMIZATION_READ
 * @permissionScope Manage v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_WRITE
 * @permissionScope Read v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CATALOG_READ_LIMITED
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId WIX_STORES.CUSTOMIZATION_READ
 * @webhook
 * @eventType wix.stores.catalog.v3.customization_created
 * @serviceIdentifier wix.stores.catalog.customization.v3.CustomizationService
 * @slug created
 */
export declare function onCustomizationCreated(
  handler: (event: CustomizationCreatedEnvelope) => void | Promise<void>
): void;

export interface CustomizationDeletedEnvelope {
  entity: Customization;
  metadata: EventMetadata;
}

/**
 * Triggered when a customization is deleted.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read customizations in v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CUSTOMIZATION_READ_LIMITED
 * @permissionScope Read v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_READ
 * @permissionScope Manage Products
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-PRODUCTS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Read Products
 * @permissionScopeId SCOPE.DC-STORES.READ-PRODUCTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Product write in v3 catalog
 * @permissionScopeId SCOPE.STORES.PRODUCT_WRITE
 * @permissionScope Read customizations in v3 catalog
 * @permissionScopeId SCOPE.STORES.CUSTOMIZATION_READ
 * @permissionScope Manage v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_WRITE
 * @permissionScope Read v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CATALOG_READ_LIMITED
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId WIX_STORES.CUSTOMIZATION_READ
 * @webhook
 * @eventType wix.stores.catalog.v3.customization_deleted
 * @serviceIdentifier wix.stores.catalog.customization.v3.CustomizationService
 * @slug deleted
 */
export declare function onCustomizationDeleted(
  handler: (event: CustomizationDeletedEnvelope) => void | Promise<void>
): void;

export interface CustomizationUpdatedEnvelope {
  entity: Customization;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a customization is updated.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read customizations in v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CUSTOMIZATION_READ_LIMITED
 * @permissionScope Read v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_READ
 * @permissionScope Manage Products
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-PRODUCTS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Read Products
 * @permissionScopeId SCOPE.DC-STORES.READ-PRODUCTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Product write in v3 catalog
 * @permissionScopeId SCOPE.STORES.PRODUCT_WRITE
 * @permissionScope Read customizations in v3 catalog
 * @permissionScopeId SCOPE.STORES.CUSTOMIZATION_READ
 * @permissionScope Manage v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_WRITE
 * @permissionScope Read v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CATALOG_READ_LIMITED
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId WIX_STORES.CUSTOMIZATION_READ
 * @webhook
 * @eventType wix.stores.catalog.v3.customization_updated
 * @serviceIdentifier wix.stores.catalog.customization.v3.CustomizationService
 * @slug updated
 */
export declare function onCustomizationUpdated(
  handler: (event: CustomizationUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a customization.
 * @param customization - Customization to create.
 * @public
 * @requiredField customization
 * @requiredField customization.choicesSettings.choices
 * @requiredField customization.customizationRenderType
 * @requiredField customization.customizationSettings
 * @requiredField customization.customizationType
 * @requiredField customization.name
 * @permissionId WIX_STORES.CUSTOMIZATION_CREATE
 * @applicableIdentity APP
 * @returns Created customization.
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.CreateCustomization
 */
export async function createCustomization(
  customization: NonNullablePaths<
    Customization,
    | `choicesSettings.choices`
    | `customizationRenderType`
    | `customizationType`
    | `freeTextInput.title`
    | `name`,
    3
  >
): Promise<
  NonNullablePaths<
    Customization,
    | `freeTextInput.minCharCount`
    | `freeTextInput.maxCharCount`
    | `freeTextInput.title`
    | `choicesSettings.choices`
    | `choicesSettings.choices.${number}.colorCode`
    | `choicesSettings.choices.${number}._id`
    | `choicesSettings.choices.${number}.choiceType`
    | `choicesSettings.choices.${number}.name`
    | `name`
    | `customizationType`
    | `customizationRenderType`,
    5
  > & {
    __applicationErrorsType?: CreateCustomizationApplicationErrors;
    __validationErrorsType?: CreateCustomizationValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ customization: customization }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'customization.choicesSettings.choices.image' },
          { path: 'customization.choicesSettings.choices.displayImage' },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.createCustomization(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'customization.choicesSettings.choices.image' },
            { path: 'customization.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )?.customization!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { customization: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['customization']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a customization.
 * @param customizationId - Customization ID.
 * @public
 * @requiredField customizationId
 * @permissionId WIX_STORES.CUSTOMIZATION_READ
 * @applicableIdentity APP
 * @returns Customization.
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.GetCustomization
 */
export async function getCustomization(
  customizationId: string,
  options?: GetCustomizationOptions
): Promise<
  NonNullablePaths<
    Customization,
    | `freeTextInput.minCharCount`
    | `freeTextInput.maxCharCount`
    | `freeTextInput.title`
    | `choicesSettings.choices`
    | `choicesSettings.choices.${number}.colorCode`
    | `choicesSettings.choices.${number}._id`
    | `choicesSettings.choices.${number}.choiceType`
    | `choicesSettings.choices.${number}.name`
    | `name`
    | `customizationType`
    | `customizationRenderType`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    customizationId: customizationId,
    fields: options?.fields,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.getCustomization(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'customization.choicesSettings.choices.image' },
            { path: 'customization.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )?.customization!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          customizationId: '$[0]',
          fields: '$[1].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['customizationId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetCustomizationOptions {
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Updates a customization.
 *
 * Each time the customization is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the customization.
 * This ensures you're working with the latest customization and prevents unintended overwrites.
 * @param _id - Customization ID.
 * @public
 * @requiredField _id
 * @requiredField customization
 * @requiredField customization.revision
 * @permissionId WIX_STORES.CUSTOMIZATION_UPDATE
 * @applicableIdentity APP
 * @returns Updated customization.
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.UpdateCustomization
 */
export async function updateCustomization(
  _id: string,
  customization: NonNullablePaths<UpdateCustomization, `revision`, 2>,
  options?: UpdateCustomizationOptions
): Promise<
  NonNullablePaths<
    Customization,
    | `freeTextInput.minCharCount`
    | `freeTextInput.maxCharCount`
    | `freeTextInput.title`
    | `choicesSettings.choices`
    | `choicesSettings.choices.${number}.colorCode`
    | `choicesSettings.choices.${number}._id`
    | `choicesSettings.choices.${number}.choiceType`
    | `choicesSettings.choices.${number}.name`
    | `name`
    | `customizationType`
    | `customizationRenderType`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      customization: { ...customization, id: _id },
      fields: options?.fields,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'customization.choicesSettings.choices.image' },
          { path: 'customization.choicesSettings.choices.displayImage' },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.updateCustomization(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'customization.choicesSettings.choices.image' },
            { path: 'customization.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )?.customization!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { customization: '$[1]' },
        explicitPathsToArguments: {
          'customization.id': '$[0]',
          fields: '$[2].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'customization', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateCustomization {
  /**
   * Free text input settings.
   *
   * > **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.
   * @immutable
   */
  freeTextInput?: FreeTextSettings;
  /**
   * Choices settings.
   *
   * > **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.
   */
  choicesSettings?: ChoicesSettings;
  /**
   * Customization ID.
   * @minLength 1
   * @maxLength 36
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the customization is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the customization.
   *
   * Ignored when creating a customization.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the customization was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the customization was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * A read-only identifier generated from the customization name.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 50
   */
  key?: string | null;
  /**
   * Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /**
   * Customization type.
   * @immutable
   */
  customizationType?: CustomizationTypeWithLiterals;
  /**
   * Customization render type.
   *
   * Defines how the customization will be displayed in the storefront.
   * @immutable
   */
  customizationRenderType?: CustomizationRenderTypeWithLiterals;
  /**
   * Number of products this customization is assigned to.
   * > **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.
   * @readonly
   */
  assignedProductsCount?: number | null;
}

export interface UpdateCustomizationOptions {
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Deletes a customization.
 *
 * > **Notes:**
 * > + **Options** (customizations with `customizationType: PRODUCT_OPTION`) can't be deleted while assigned to any product because they affect product variants and inventory.
 * > + **Modifiers** can be deleted freely. When deleted, they are automatically removed from all products that reference them.
 * @param customizationId - Customization ID.
 * @public
 * @requiredField customizationId
 * @permissionId WIX_STORES.CUSTOMIZATION_DELETE
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.DeleteCustomization
 */
export async function deleteCustomization(customizationId: string): Promise<
  void & {
    __validationErrorsType?: DeleteCustomizationValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    customizationId: customizationId,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.deleteCustomization(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { customizationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['customizationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of up to 100 customizations, given the provided filtering, sorting, and cursor paging.
 * Pass supported values to the `fields` array in the request to include those fields in the response.
 *
 *
 * Query Customizations runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `cursorPaging.limit` is `100`
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 * @public
 * @permissionId WIX_STORES.CUSTOMIZATION_READ
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.QueryCustomizations
 */
export function queryCustomizations(
  options?: QueryCustomizationsOptions
): CustomizationsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Customization,
    'CURSOR',
    QueryCustomizationsRequest,
    QueryCustomizationsResponse
  >({
    func: async (payload: QueryCustomizationsRequest) => {
      const reqOpts =
        ambassadorWixStoresCatalogV3Customization.queryCustomizations({
          ...payload,
          ...(options ?? {}),
        });

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryCustomizationsRequest['query']) => {
      const args = [query, options] as [
        QueryCustomizationsRequest['query'],
        QueryCustomizationsOptions
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryCustomizationsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [
              { path: 'customizations.choicesSettings.choices.image' },
              { path: 'customizations.choicesSettings.choices.displayImage' },
            ],
          },
        ])
      );

      return {
        items: transformedData?.customizations,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

export interface QueryCustomizationsOptions {
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[] | undefined;
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface CustomizationsQueryResult extends QueryCursorResult {
  items: Customization[];
  query: CustomizationsQueryBuilder;
  next: () => Promise<CustomizationsQueryResult>;
  prev: () => Promise<CustomizationsQueryResult>;
}

export interface CustomizationsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'customizationType'
      | 'customizationRenderType',
    value: any
  ) => CustomizationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'customizationType'
      | 'customizationRenderType',
    value: any
  ) => CustomizationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => CustomizationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => CustomizationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => CustomizationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => CustomizationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'name',
    value: string
  ) => CustomizationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'customizationType'
      | 'customizationRenderType',
    value: any[]
  ) => CustomizationsQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'customizationType'
      | 'customizationRenderType',
    value: any
  ) => CustomizationsQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'customizationType'
      | 'customizationRenderType',
    value: boolean
  ) => CustomizationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<'_id' | '_createdDate' | '_updatedDate' | 'name'>
  ) => CustomizationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<'_id' | '_createdDate' | '_updatedDate' | 'name'>
  ) => CustomizationsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => CustomizationsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => CustomizationsQueryBuilder;
  find: () => Promise<CustomizationsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.QueryCustomizations
 * @requiredField query
 */
export async function typedQueryCustomizations(
  query: CustomizationQuery,
  options?: QueryCustomizationsOptions
): Promise<
  NonNullablePaths<
    QueryCustomizationsResponse,
    | `customizations`
    | `customizations.${number}.freeTextInput.minCharCount`
    | `customizations.${number}.freeTextInput.maxCharCount`
    | `customizations.${number}.freeTextInput.title`
    | `customizations.${number}.name`
    | `customizations.${number}.customizationType`
    | `customizations.${number}.customizationRenderType`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    ...options,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.queryCustomizations(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'customizations.choicesSettings.choices.image' },
            { path: 'customizations.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CustomizationQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_createdDate', '_id', '_updatedDate', 'name'];
      operators: '*';
      sort: 'BOTH';
    },
    {
      fields: ['customizationRenderType', 'customizationType'];
      operators: '*';
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Customization,
  CustomizationQuerySpec
>;
export type CustomizationQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      Customization,
      CustomizationQuerySpec,
      CustomizationQuery
    >(),
  },
};

/**
 * Creates multiple customizations.
 * @param customizations - Customizations to create.
 * @public
 * @requiredField customizations
 * @permissionId WIX_STORES.CUSTOMIZATION_CREATE
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.BulkCreateCustomizations
 */
export async function bulkCreateCustomizations(
  customizations: Customization[],
  options?: BulkCreateCustomizationsOptions
): Promise<
  NonNullablePaths<
    BulkCreateCustomizationsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.customization.freeTextInput.minCharCount`
    | `results.${number}.customization.freeTextInput.maxCharCount`
    | `results.${number}.customization.freeTextInput.title`
    | `results.${number}.customization.name`
    | `results.${number}.customization.customizationType`
    | `results.${number}.customization.customizationRenderType`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkCreateCustomizationsApplicationErrors;
    __validationErrorsType?: BulkCreateCustomizationsValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      customizations: customizations,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'customizations.choicesSettings.choices.image' },
          { path: 'customizations.choicesSettings.choices.displayImage' },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.bulkCreateCustomizations(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.customization.choicesSettings.choices.image' },
            {
              path: 'results.customization.choicesSettings.choices.displayImage',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          customizations: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['customizations', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateCustomizationsOptions {
  /**
   * Whether to return the full customization entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

/**
 * Adds choices to a customization.
 * @param customizationId - Customization ID.
 * @param choices - Choices to add.
 * @public
 * @requiredField choices
 * @requiredField customizationId
 * @permissionId WIX_STORES.CUSTOMIZATION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.AddCustomizationChoices
 */
export async function addCustomizationChoices(
  customizationId: string,
  choices: Choice[],
  options?: AddCustomizationChoicesOptions
): Promise<
  NonNullablePaths<
    AddCustomizationChoicesResponse,
    | `customization.freeTextInput.minCharCount`
    | `customization.freeTextInput.maxCharCount`
    | `customization.freeTextInput.title`
    | `customization.choicesSettings.choices`
    | `customization.choicesSettings.choices.${number}.colorCode`
    | `customization.choicesSettings.choices.${number}._id`
    | `customization.choicesSettings.choices.${number}.choiceType`
    | `customization.choicesSettings.choices.${number}.name`
    | `customization.name`
    | `customization.customizationType`
    | `customization.customizationRenderType`,
    6
  > & {
    __applicationErrorsType?: AddCustomizationChoicesApplicationErrors;
    __validationErrorsType?: AddCustomizationChoicesValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      customizationId: customizationId,
      choices: choices,
      fields: options?.fields,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'choices.image' }, { path: 'choices.displayImage' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.addCustomizationChoices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'customization.choicesSettings.choices.image' },
            { path: 'customization.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          customizationId: '$[0]',
          choices: '$[1]',
          fields: '$[2].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['customizationId', 'choices', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddCustomizationChoicesOptions {
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Sets a customization's choices. Any and all existing choices will be overridden.
 *
 * > **Note:** A choice cannot be overridden if it is assigned to one or more products.
 * @param customizationId - Customization ID.
 * @param choices - Choices to set.
 * @public
 * @requiredField choices
 * @requiredField customizationId
 * @permissionId WIX_STORES.CUSTOMIZATION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.SetCustomizationChoices
 */
export async function setCustomizationChoices(
  customizationId: string,
  choices: Choice[],
  options?: SetCustomizationChoicesOptions
): Promise<
  NonNullablePaths<
    SetCustomizationChoicesResponse,
    | `customization.freeTextInput.minCharCount`
    | `customization.freeTextInput.maxCharCount`
    | `customization.freeTextInput.title`
    | `customization.choicesSettings.choices`
    | `customization.choicesSettings.choices.${number}.colorCode`
    | `customization.choicesSettings.choices.${number}._id`
    | `customization.choicesSettings.choices.${number}.choiceType`
    | `customization.choicesSettings.choices.${number}.name`
    | `customization.name`
    | `customization.customizationType`
    | `customization.customizationRenderType`,
    6
  > & {
    __applicationErrorsType?: SetCustomizationChoicesApplicationErrors;
    __validationErrorsType?: SetCustomizationChoicesValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      customizationId: customizationId,
      choices: choices,
      fields: options?.fields,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'choices.image' }, { path: 'choices.displayImage' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.setCustomizationChoices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'customization.choicesSettings.choices.image' },
            { path: 'customization.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          customizationId: '$[0]',
          choices: '$[1]',
          fields: '$[2].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['customizationId', 'choices', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SetCustomizationChoicesOptions {
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Removes choices from a customization.
 *
 * > **Note:** A choice can't be removed if it's assigned to one or more product variants. This applies to option choices only; modifier choices can be removed freely.
 * @param customizationId - Customization ID.
 * @param choiceIds - IDs of choices to remove.
 * @public
 * @requiredField choiceIds
 * @requiredField customizationId
 * @permissionId WIX_STORES.CUSTOMIZATION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.RemoveCustomizationChoices
 */
export async function removeCustomizationChoices(
  customizationId: string,
  choiceIds: string[],
  options?: RemoveCustomizationChoicesOptions
): Promise<
  NonNullablePaths<
    RemoveCustomizationChoicesResponse,
    | `customization.freeTextInput.minCharCount`
    | `customization.freeTextInput.maxCharCount`
    | `customization.freeTextInput.title`
    | `customization.choicesSettings.choices`
    | `customization.choicesSettings.choices.${number}.colorCode`
    | `customization.choicesSettings.choices.${number}._id`
    | `customization.choicesSettings.choices.${number}.choiceType`
    | `customization.choicesSettings.choices.${number}.name`
    | `customization.name`
    | `customization.customizationType`
    | `customization.customizationRenderType`,
    6
  > & {
    __applicationErrorsType?: RemoveCustomizationChoicesApplicationErrors;
    __validationErrorsType?: RemoveCustomizationChoicesValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    customizationId: customizationId,
    choiceIds: choiceIds,
    revision: options?.revision,
    fields: options?.fields,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.removeCustomizationChoices(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'customization.choicesSettings.choices.image' },
            { path: 'customization.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          customizationId: '$[0]',
          choiceIds: '$[1]',
          revision: '$[2].revision',
          fields: '$[2].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['customizationId', 'choiceIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RemoveCustomizationChoicesOptions {
  /** Customization revision. */
  revision?: string;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Updates properties of existing choices in a customization.
 * Choices are identified by their IDs. Only the fields specified in `fieldMask` are updated.
 *
 * Currently supports updating `primaryChoiceIds`.
 * @param customizationId - Customization ID.
 * @param revision - Revision number, which increments by 1 each time the customization is updated.
 * To prevent conflicting changes,
 * the current revision must be passed when updating the customization.
 * @internal
 * @documentationMaturity preview
 * @requiredField customizationId
 * @requiredField options.choices
 * @requiredField options.choices.choice
 * @requiredField revision
 * @permissionId WIX_STORES.CUSTOMIZATION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.UpdateCustomizationChoices
 */
export async function updateCustomizationChoices(
  customizationId: string,
  revision: string,
  options?: NonNullablePaths<
    UpdateCustomizationChoicesOptions,
    `choices` | `choices.${number}.choice`,
    4
  >
): Promise<
  NonNullablePaths<
    UpdateCustomizationChoicesResponse,
    | `customization.freeTextInput.minCharCount`
    | `customization.freeTextInput.maxCharCount`
    | `customization.freeTextInput.title`
    | `customization.choicesSettings.choices`
    | `customization.choicesSettings.choices.${number}.colorCode`
    | `customization.choicesSettings.choices.${number}._id`
    | `customization.choicesSettings.choices.${number}.choiceType`
    | `customization.choicesSettings.choices.${number}.name`
    | `customization.name`
    | `customization.customizationType`
    | `customization.customizationRenderType`,
    6
  > & {
    __applicationErrorsType?: UpdateCustomizationChoicesApplicationErrors;
    __validationErrorsType?: UpdateCustomizationChoicesValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      customizationId: customizationId,
      revision: revision,
      choices: options?.choices,
      fields: options?.fields,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'choices.choice.image' },
          { path: 'choices.choice.displayImage' },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.updateCustomizationChoices(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'customization.choicesSettings.choices.image' },
            { path: 'customization.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          customizationId: '$[0]',
          revision: '$[1]',
          choices: '$[2].choices',
          fields: '$[2].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['customizationId', 'revision', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateCustomizationChoicesOptions {
  /**
   * Choices to update. Each item must include a `choice` with a valid `id` matching an existing choice in the customization.
   * @minSize 1
   * @maxSize 100
   */
  choices: MaskedChoice[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Reorders a customization's choices. The new order is persisted and reflected in the customization's choices list.
 * Calling this endpoint sets `choicesSettings.sortOrder` to `MANUAL`.
 *
 * To control how choices are sorted in storefront filter panels, use [Update Customization](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/customizations-v3/update-customization) to set the `choicesSettings.sortOrder` field.
 * @param customizationId - Customization ID.
 * @param revision - Revision number, which increments by 1 each time the customization is updated. To prevent conflicting changes, the current revision must be passed when updating the customization.
 * @internal
 * @documentationMaturity preview
 * @requiredField customizationId
 * @requiredField options.choiceIds
 * @requiredField revision
 * @permissionId WIX_STORES.CUSTOMIZATION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.ReorderCustomizationChoices
 */
export async function reorderCustomizationChoices(
  customizationId: string,
  revision: string,
  options?: NonNullablePaths<ReorderCustomizationChoicesOptions, `choiceIds`, 2>
): Promise<
  NonNullablePaths<
    ReorderCustomizationChoicesResponse,
    | `customization.freeTextInput.minCharCount`
    | `customization.freeTextInput.maxCharCount`
    | `customization.freeTextInput.title`
    | `customization.choicesSettings.choices`
    | `customization.choicesSettings.choices.${number}.colorCode`
    | `customization.choicesSettings.choices.${number}._id`
    | `customization.choicesSettings.choices.${number}.choiceType`
    | `customization.choicesSettings.choices.${number}.name`
    | `customization.name`
    | `customization.customizationType`
    | `customization.customizationRenderType`,
    6
  > & {
    __applicationErrorsType?: ReorderCustomizationChoicesApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    customizationId: customizationId,
    revision: revision,
    choiceIds: options?.choiceIds,
    fields: options?.fields,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.reorderCustomizationChoices(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'customization.choicesSettings.choices.image' },
            { path: 'customization.choicesSettings.choices.displayImage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          customizationId: '$[0]',
          revision: '$[1]',
          choiceIds: '$[2].choiceIds',
          fields: '$[2].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['customizationId', 'revision', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ReorderCustomizationChoicesOptions {
  /**
   * Choice IDs in the desired display order. Must contain exactly the same set of IDs as the customization's current choices — no additions or removals.
   * @format GUID
   * @minSize 1
   * @maxSize 200
   */
  choiceIds: string[];
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Adds choices to multiple customizations.
 * @param customizationsChoices - List of customization IDs and choices.
 * @public
 * @requiredField customizationsChoices
 * @requiredField customizationsChoices.choices
 * @requiredField customizationsChoices.customizationId
 * @permissionId WIX_STORES.CUSTOMIZATION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.BulkAddCustomizationChoices
 */
export async function bulkAddCustomizationChoices(
  customizationsChoices: NonNullablePaths<
    CustomizationChoices,
    `choices` | `customizationId`,
    2
  >[],
  options?: BulkAddCustomizationChoicesOptions
): Promise<
  NonNullablePaths<
    BulkAddCustomizationChoicesResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.customization.freeTextInput.minCharCount`
    | `results.${number}.customization.freeTextInput.maxCharCount`
    | `results.${number}.customization.freeTextInput.title`
    | `results.${number}.customization.name`
    | `results.${number}.customization.customizationType`
    | `results.${number}.customization.customizationRenderType`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkAddCustomizationChoicesApplicationErrors;
    __validationErrorsType?: BulkAddCustomizationChoicesValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      customizationsChoices: customizationsChoices,
      returnEntity: options?.returnEntity,
      fields: options?.fields,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'customizationsChoices.choices.image' },
          { path: 'customizationsChoices.choices.displayImage' },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.bulkAddCustomizationChoices(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.customization.choicesSettings.choices.image' },
            {
              path: 'results.customization.choicesSettings.choices.displayImage',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          customizationsChoices: '$[0]',
          returnEntity: '$[1].returnEntity',
          fields: '$[1].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['customizationsChoices', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkAddCustomizationChoicesOptions {
  /**
   * Whether to return the full customization entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Updates multiple customizations.
 *
 * Each time the customization is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the customization.
 * This ensures you're working with the latest customization and prevents unintended overwrites.
 * @param customizations - List of customizations to update.
 * @public
 * @requiredField customizations
 * @requiredField customizations.customization._id
 * @requiredField customizations.customization.name
 * @requiredField customizations.customization.revision
 * @permissionId WIX_STORES.CUSTOMIZATION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.customization.v3.CustomizationService.BulkUpdateCustomizations
 */
export async function bulkUpdateCustomizations(
  customizations: NonNullablePaths<
    MaskedCustomization,
    `customization._id` | `customization.name` | `customization.revision`,
    3
  >[],
  options?: BulkUpdateCustomizationsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateCustomizationsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.customization.freeTextInput.minCharCount`
    | `results.${number}.customization.freeTextInput.maxCharCount`
    | `results.${number}.customization.freeTextInput.title`
    | `results.${number}.customization.name`
    | `results.${number}.customization.customizationType`
    | `results.${number}.customization.customizationRenderType`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      customizations: customizations,
      returnEntity: options?.returnEntity,
      fields: options?.fields,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          {
            path: 'customizations.customization.choicesSettings.choices.image',
          },
          {
            path: 'customizations.customization.choicesSettings.choices.displayImage',
          },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixStoresCatalogV3Customization.bulkUpdateCustomizations(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.customization.choicesSettings.choices.image' },
            {
              path: 'results.customization.choicesSettings.choices.displayImage',
            },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          customizations: '$[0]',
          returnEntity: '$[1].returnEntity',
          fields: '$[1].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['customizations', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateCustomizationsOptions {
  /**
   * Whether to return the full customization entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /**
   * Fields to include in the response.
   *
   * Supported values: `ASSIGNED_PRODUCTS_COUNT`
   * @maxSize 100
   */
  fields?: RequestedFieldsWithLiterals[];
}
